/**
 * @file LegalCertificatesGroupBlock.tsx
 * @description Состав автоматической группы «Сертификаты и декларации»
 * (phase19.16, SB141; uiux-legal-steps-b-c §6.4): список собирается сам из
 * карточек производителей — руками не правится (PUT состава → 422
 * `legal_group_automatic`).
 *
 * Правило отбора, счётчики «На сайте: N · не показываются: M», карточки по
 * производителям (вид, номер, срок, статус на сайте; «Открыть производителя ›»),
 * пустое состояние. Поля `certificates` / `certificateStats` читаются
 * терпимо (`legal-certificates.ts`): старый API без них — только правило и
 * ссылка на производителей.
 */

import type { LegalDocumentDto } from "@chaiberi/shared";
import { Link, useParams } from "react-router";

import { NoticeStrip } from "../ui/NoticeStrip";
import { StatusPill } from "../ui/StatusPill";
import { certificateStatsText, groupCertificatesByProducer, readCertificateStats, readGroupCertificates, type LegalGroupCertificate } from "../../lib/legal/legal-certificates";
import { formatLegalDate } from "../../lib/legal/legal-doc-view";

interface Props {
  doc: LegalDocumentDto;
}

/** «Декларация № ЕАЭС N RU Д-… · до 12.10.2027». */
function certLine(c: LegalGroupCertificate): string {
  const name = [c.typeLabel, c.number ? `№ ${c.number}` : ""].filter(Boolean).join(" ");
  return c.validUntil ? `${name} · до ${formatLegalDate(c.validUntil)}` : `${name} · бессрочный`;
}

export function LegalCertificatesGroupBlock({ doc }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const producersHref = `/shop/${slug}/catalog/producers`;
  const certificates = readGroupCertificates(doc);
  const stats = readCertificateStats(doc, certificates);
  const producers = certificates ? groupCertificatesByProducer(certificates) : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-on-surface">Состав — собирается сам</span>
        <p className="type-body text-on-surface-variant">
          Из карточек производителей: с файлом, отмеченные «на сайте», действующие или бессрочные. Просроченные на сайте не показываются.
        </p>
        {stats ? (
          <p className="type-body text-on-surface">
            {certificateStatsText(stats)}
          </p>
        ) : null}
      </div>

      {doc.isPublished && (stats ? stats.onSite === 0 : !doc.visibleOnSite) ? (
        <NoticeStrip tone="info">
          На сайте сейчас пусто — страница и ссылки на неё скрыты. Добавьте файл и срок в карточке производителя: Каталог → Производители.
        </NoticeStrip>
      ) : null}

      {certificates && certificates.length === 0 ? (
        <p className="type-body text-on-surface-variant">Сертификатов пока нет — добавьте их в карточках производителей.</p>
      ) : null}

      {producers.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {producers.map((p) => (
            <li key={p.producerId || p.producerName} className="flex flex-col gap-1 rounded-xl p-3 [box-shadow:inset_0_0_0_1px_var(--border)]">
              <span className="type-body font-medium text-on-surface">{p.producerName}</span>
              <ul className="flex flex-col divide-y divide-[var(--border)]">
                {p.certificates.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2">
                    <span className="type-body min-w-0 flex-1 break-words text-on-surface">{certLine(c)}</span>
                    <StatusPill meta={{ tone: c.siteStatus.tone, label: c.siteStatus.label }} />
                  </li>
                ))}
              </ul>
              {p.producerId ? (
                <Link to={`${producersHref}/${p.producerId}`} className="type-body inline-flex min-h-12 items-center self-start font-medium text-primary hover:underline">
                  Открыть производителя ›
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <Link to={producersHref} className="type-body inline-flex min-h-12 items-center self-start font-medium text-primary hover:underline">
        К производителям →
      </Link>
    </div>
  );
}
